import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import 'url-search-params-polyfill';
import {
  PageHeader,
  TabContainer,
  Tab,
  CompHeading,
  FootNote,
  SimpleDataTable,
  Section,
  AgencyInformation,
  toLineItem,
  slugify,
  toSectionsURL,
  makeBreadcrumb,
  ExpTreeTableWithButtons,
  ExpTreeTable
} from '@budget-monorepo/shared-ui';

import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';
import AppropriationFilter from '../components/AppropriationFilter';
import SubTab from '../components/SubTab';

delete TabContainer.contextType;

// eslint-disable-next-line react/prefer-stateless-function
class Appropriation extends Component {
  constructor(props) {
    super(props);
    const { data } = this.props;
    const { content } = data;
    // for the summary detail nested tree table
    const summaryDetails = content.budgetSummary && content.budgetSummary.summaryDetails;
    const allData = summaryDetails && summaryDetails.data && this.getInnerData(summaryDetails);
    // for the tracking details nested tree table
    const trackingDetails = content.budgetTracking && content.budgetTracking.trackingDetails;
    const allTrackingData = trackingDetails && trackingDetails.data && this.getInnerData(trackingDetails);
    this.state = {
      summaryData: {
        ...allData
      },
      trackingData: {
        ...allTrackingData
      }
    };
  }

  onTabChange = (tabRef, curLocation) => {
    const { location } = this.props;
    if (global.window && curLocation.href) {
      const tab = tabRef.current.innerText;
      const { pathname } = location;
      const url = `${pathname}?tab=${slugify(tab)}`;
      // eslint-disable-next-line no-undef
      window.history.replaceState({}, '', url);
    }
  };

  getRelPath = () => {
    const { location } = this.props;
    const path = location.pathname;
    const pathPrefix = process.env.GATSBY_PATH_PREFIX;
    return path.replace(pathPrefix, '');
  };

  // for the summary detail nested tree table
  getInnerData = (table) => {
    const { data } = this.props;
    const treeTable = JSON.parse(JSON.stringify(table));
    const allData = [];
    let i = 0;
    const treeMax = treeTable.data.length;
    for (; i < treeMax; i += 1) {
      const item = treeTable.data[i];
      if (item && item.budgetTracking) {
        const {
          govLevelOne, govLevelTwo, govLevelThree, lineItemNumber
        } = item;
        const slug = toSectionsURL({
          href: data.content.fields.baseSectionURL,
          sections: govLevelOne === govLevelTwo ? [govLevelOne, govLevelThree, lineItemNumber] : [govLevelOne, govLevelTwo, govLevelThree, lineItemNumber]
        });
        const lineItem = {
          ...item,
          ...item.budgetTracking.data[0],
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          lineItemNumber: <Link to={slug}>{toLineItem(item.lineItemNumber)}</Link>
        };
        allData.push({ ...lineItem });
      }
    }
    treeTable.data = allData;
    return{
      treeTable,
      treeTableData: allData
    };
  };

  urlToTabIndex = (location) => {
    if (!location.href) {
      return null;
    }
    const { data } = this.props;
    const url = new URL(location.href);
    const params = new URLSearchParams(url.search);
    const param = params.get('tab');
    let index = 0;
    data.content.tabs.every((tab, tabIndex) => {
      if (slugify(tab.name) === param) {
        index = tabIndex;
        return false;
      }
      return true;
    });
    return index;
  };

  render() {
    const { data, location } = this.props;
    const { content, site } = data;
    const {
      title, tabs, govLevelOne, govLevelTwo, govLevelThree, name, agencyInformation, orgChart, budgetSummary, budgetTracking, parent
    } = content;
    // eslint-disable-next-line camelcase
    const { modifiedTime, mg_mod_date, mg_mod_datetime } = parent;
    const { pageTitle, breadcrumbprops } = makeBreadcrumb({
      href: content.fields.baseSectionURL,
      text: 'Enacted Budget',
      sections: [govLevelOne, govLevelTwo, govLevelThree]
    });
    const { summaryData, trackingData } = this.state;
    const expandableTreeTableWithButtonsProps = {
      defaultExpLevel: 1,
      expTreeTableProps: {
        table: summaryData.treeTable,
        data: summaryData.treeTableData,
        maxColWidth: 135,
        pivotBy: ['appropriationType'],
        aggregateBy: ['gaa'],
        subComponent: 'lineItemLanguage',
        subIndex: 2
      },
      treeTableButtonsProps: {
        a11yTitle: title,
        headerLevel: 2,
        buttons: [{
          info: 'View the line items grouped by appropriation type.',
          text: 'View by line item',
          accessor: 'appropriationType',
          expLevel: 0
        }, {
          info: 'View all of the line item language.',
          text: 'View line item language',
          accessor: 'lineItemLanguage',
          expLevel: 1
        }]
      }
    };
    const trackingExpTreeTableProps = {
      table: trackingData.treeTable,
      data: trackingData.treeTableData,
      maxColWidth: 135,
      pivotBy: ['appropriationType'],
      aggregateBy: ['gaa']
    };
    const summaryDetails = budgetSummary && budgetSummary.summaryDetails;
    const trackingDetails = budgetTracking && budgetTracking.trackingDetails;
    const relPath = this.getRelPath();
    const layoutProps = {
      activeIndex: 1,
      pre: () => <AppropriationFilter path={relPath} />,
      above: () => <PageHeader level={1} title={title} />,
      pageTitle,
      breadcrumbprops,
      dataTimeStamp: modifiedTime,
      buildTimeStamp: site.buildTime
    };
    const children = tabs.map((tab, index) => {
      const tabKey = `appropriation-tab.${tab.name}.${index}`;
      const tabProps = {
        key: tabKey,
        title: tab.name
      };
      const tabValue = tab.value;
      const tabObj = content[tabValue];
      const hasSubTabs = tabObj && tabObj.subTabs;
      const hasTable = tabObj && tabObj.headers && tabObj.data;
      let tabTable;
      let footnotes;
      if (hasTable) {
        const tabTableData = [];
        let i = 0;
        const tabMax = tabObj.data.length;
        for (; i < tabMax; i += 1) {
          const d = tabObj.data[i];
          if (d) {
            const {
              // eslint-disable-next-line no-shadow
              organization, govLevelOne, govLevelTwo, govLevelThree, lineItemNumber, ...rest
            } = d;
            const govLevel = govLevelOne || (govLevelTwo || govLevelThree);
            let tableData = null;
            // If the page government entity is not equal to the table record, make the organization field a link.
            if (organization && organization !== title) {
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              tableData = { organization: (<Link to={`${relPath}/${slugify(govLevel)}?tab=${slugify(tab.name)}`}>{organization}</Link>), ...rest };
            }
            if (organization && organization === title) {
              // If the page government entity is equal to the table record, do not make the organization record a drillable link.
              tableData = { organization, ...rest };
            }
            if (lineItemNumber) {
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              tableData = { lineItemNumber: (<Link to={`${relPath}/${lineItemNumber}`}>{toLineItem(lineItemNumber)}</Link>), ...rest };
            }
            if (tableData) {
              tabTableData.push(tableData);
            }
          }
        }
        tabTable = { ...tabObj };
        tabTable.data = tabTableData;
        footnotes = tabTable.footnotes;
      }
      const tabContent = [];
      if (hasSubTabs) {
        tabContent.push(<SubTab key={`${tabKey}.content`} title={title} parentTab={tab} tabObj={tabObj} path={relPath} location={location} footnotes={tabObj.footnotes} />);
      }
      if (hasTable) {
        tabContent.push((
          <Fragment key={`${tabKey}.content`}>
            {tabValue !== 'budgetTracking' && (
              <Section>
                <SimpleDataTable title={`${title} ${tab.name}`} table={tabTable} maxColWidth={200} download={`${location.pathname}-${tab.name}`} />
              </Section>
            )}
            { summaryDetails && tabValue === 'budgetSummary' && (
              <Section>
                <Fragment>
                  <CompHeading level={3} title="Summary Details" />
                  <ExpTreeTableWithButtons {...expandableTreeTableWithButtonsProps} />
                </Fragment>
              </Section>
            )}
            { !trackingDetails && tabValue === 'budgetTracking' && (
              <Section>
                <SimpleDataTable title={`${title} ${tab.name}`} table={tabTable} maxColWidth={200} download={`${location.pathname}-${tab.name}`} />
              </Section>
            )}
            { trackingDetails && tabValue === 'budgetTracking' && (
              <Section>
                <Fragment>
                  <ExpTreeTable {...trackingExpTreeTableProps} />
                </Fragment>
              </Section>
            )}
            { footnotes && footnotes.map((footnote) => (
              <FootNote key={`appropriations.footnote.${footnote.index}`} index={footnote.index}>{footnote.text}</FootNote>
            ))}
          </Fragment>
        ));
      }
      if (tabValue === 'orgChart') {
        // Note, the below casts the orgchart url to lower case to fix 404s as all current url are such.
        // If you want to remove this functionality update the src prop to src={orgChart}
        tabContent.push((
          <iframe key={`${tabKey}.content`} title={`${name} Org Chart`} src={orgChart.toLowerCase()} style={{ height: 2100, width: '100%' }} />
        ));
      }
      if (tabValue === 'agencyInformation' && agencyInformation) {
        tabContent.push((
          <AgencyInformation {...agencyInformation} pageTitle={pageTitle} />
        ));
      }
      return(
        <Tab {...tabProps}>
          {tabContent}
        </Tab>
      );
    });

    const metaTitle = `Enacted budget for ${title}`;
    const metaTabs = tabs.map((tab) => tab.name).join(', ');
    const metaDescription = `Browse ${metaTabs} data for ${title}.`;
    return(
      <Fragment>
        <HelmetData {...{
          metaTitle,
          metaDescription,
          mg_mod_date,
          mg_mod_datetime
        }}
        />
        <Layout {...layoutProps}>
          <Section>
            {global.window && (
              <TabContainer defaultTab={this.urlToTabIndex(location)} onTabChange={(tabRef) => this.onTabChange(tabRef, location)}>
                {children}
              </TabContainer>
            )}
          </Section>
        </Layout>
      </Fragment>
    );
  }
}

Appropriation.propTypes = {
  data: PropTypes.object,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string
  })
};


export default Appropriation;

export const query = graphql`
  query($slug: String!) {
    site {
      ...SiteMetadata
    }
    content: appropriationsJson(fields: { slug: { eq: $slug } }) {

      fields {
        baseSectionURL
      }

      title

      govLevelOne
      govLevelTwo
      govLevelThree

      # Get the tabs rendered on the given appropriations page
      tabs {
        name
        value
      }

      # Get the budget summary table for the budget summary tab
      ...budgetSummary
      # Get the summary details table (line items) for the budget summary tab
      ...summaryDetails

      ...budgetTracking
      ...trackingDetails

      ...historicalBudget

      # Get the historical spending tabs and its nested subtabs headers and names
      ...historicalSpendingTabs
      # Get the historical spending subtabs table data
      ...summary
      ...wagesAndSalaries
      ...employeeBenefits
      ...operatingExpenses
      ...grantsAndSubsidies
      ...safetyNet
      ...other

      # Get the agencyInformation tab content
      ...agencyInformation

      # Get the org chart tab content
      orgChart

      # Get the last modified timestamp for the file powering the appropriation page.
      ...AppropriationsJsonDataTimeStamp
    }
}
`;
